import React from "react";
import {
  Input,
  Label,
  Radio,
} from "@trussle/external-components";
import { useCalculatorStore } from "../store";
import { convertCurrencyStringToFloat } from "../../CalculatorPanels/calculatorMethods";
import { trackEvent } from "../../../../helpers/analytics";

import "./styles.scss";
import { ICalculateStampDutyParams, calculateStampDuty } from "../../CalculatorPanels/StampDutyCalculatorPanel/calculateStampDuty";
import {
  IStampDutyCountry,
  IStampDutyYesNoFirstTimeBuyer,
  IStampDutyYesNoOnlyProperty,
} from "../../CalculatorPanels/StampDutyCalculatorPanel/forms/types";
import { formatNumberWithoutDecimals } from "../../CalculatorPanels/StampDutyCalculatorPanel/StampDutyCalculatorResults";
import { CalculatorTabLayout } from "../CalculatorTabLayout";

const Disclaimer = (
  <div className="calculator-panel-v2__disclaimer">
    Our stamp duty calculation is for guidance only.
    <br />
    Tax rules can change and people's circumstances are different.
  </div>
);

export const StampDutyCalculator: React.FunctionComponent = () => {
  const {
    propertyValue,
    firstTimeBuyer,
    onlyProperty,
    locationProperty,
    setPropertyValue,
    setFirstTimeBuyer,
    setOnlyProperty,
    setLocationProperty,
  } = useCalculatorStore();

  const shouldDisabledIsOnlyProperty = firstTimeBuyer === IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER;
  const isOnlyPropertyWithDisable = shouldDisabledIsOnlyProperty ? IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY : onlyProperty;

  const charges = (
    (onlyProperty !== undefined || shouldDisabledIsOnlyProperty)
    && firstTimeBuyer !== undefined
    && propertyValue !== ""
    && locationProperty !== undefined)
    ? calculateStampDuty({
      country: locationProperty,
      propertyValue: convertCurrencyStringToFloat(propertyValue),
      isFirstTimeBuyer: firstTimeBuyer !== undefined ? firstTimeBuyer === IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER : undefined,
      isOnlyProperty: isOnlyPropertyWithDisable !== undefined
        ? isOnlyPropertyWithDisable === IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY : undefined,
    } as ICalculateStampDutyParams) : "";

  const form = (
    <>
      <Input
        betterV2
        variant="currency"
        name="Property price"
        label="Property price"
        inputSize="large"
        id="stamp-duty-calculator-property-price"
        data-testid="stamp-duty-calculator-property-price"
        classOverrides={{ inputContainer: "calculator-input" }}
        value={propertyValue}
        required
        onChange={(e) => {
          setPropertyValue(e.target.value);
        }}
      />
      <Label
        title="Where are you buying?"
        className="calculator-label-better-v2"
        htmlFor="location"
      />
      <div className="stamp-duty-calculator__radio-last-two-inline">
        <Radio
          name="location"
          options={[
            { code: IStampDutyCountry.ENG_NI, description: "England or Northern Ireland" },
            { code: IStampDutyCountry.SCOT, description: "Scotland" },
            { code: IStampDutyCountry.WALES, description: "Wales" },
          ]}
          className="calculator-input"
          data-testid="stamp-duty-calculator-location"
          inline={false}
          isValid
          value={locationProperty}
          variant="v2"
          onChange={(e) => {
            trackEvent("Country Selected", {
              module: "Stamp Duty Calculator",
              value: e.target.value,
              element_type: "Radio",
            });
            setLocationProperty(IStampDutyCountry[e.target.value as keyof typeof IStampDutyCountry]);
          }}
        />
      </div>
      <Label
        title="Are you a first time buyer?"
        className="calculator-label-better-v2"
        htmlFor="firstProperty"
      />
      <div className="stamp-duty-calculator__radio-two-inline">
        <Radio
          name="firstProperty"
          options={[
            { code: IStampDutyYesNoFirstTimeBuyer.YES_FIRST_TIME_BUYER, description: "Yes" },
            { code: IStampDutyYesNoFirstTimeBuyer.NO_FIRST_TIME_BUYER, description: "No" },
          ]}
          className="calculator-input"
          data-testid="stamp-duty-calculator-firstProperty"
          inline={false}
          isValid
          value={firstTimeBuyer}
          variant="v2"
          onChange={(e) => {
            trackEvent("First time buyer selected", {
              module: "Stamp Duty Calculator betterV2",
              value: e.target.value,
              element_type: "Radio",
            });
            setFirstTimeBuyer(e.target.value);
          }}
        />
      </div>
      <Label
        title="Will this be your only property?"
        className="calculator-label-better-v2"
        htmlFor="onlyProperty"
      />
      <div className="stamp-duty-calculator__radio-two-inline">
        <Radio
          name="onlyProperty"
          options={[
            { code: IStampDutyYesNoOnlyProperty.YES_ONLY_PROPERTY, description: "Yes" },
            { code: IStampDutyYesNoOnlyProperty.NO_ONLY_PROPERTY, description: "No" },
          ]}
          className="calculator-input"
          data-testid="stamp-duty-calculator-onlyProperty"
          inline={false}
          isValid
          value={onlyProperty}
          disabled={shouldDisabledIsOnlyProperty}
          variant="v2"
          onChange={(e) => {
            trackEvent("Only property selected", {
              module: "Stamp Duty Calculator betterV2",
              value: e.target.value,
              element_type: "Radio",
            });
            setOnlyProperty(e.target.value);
          }}
        />
      </div>
    </>
  );

  const result = (
    <div className="calculator-panel-v2__horizontal-line calculator-panel-v2__result__header">
      <h3>Stamp duty to pay</h3>
      <span>
        {charges !== "" ? formatNumberWithoutDecimals(charges) : ""}
      </span>
    </div>
  );

  return (
    <CalculatorTabLayout
      variant="StampDuty"
      form={form}
      result={result}
      disclaimer={Disclaimer}
    />
  );
};
